import React from 'react';
import type { StoredDream } from '../services/dream-storage';
import type { AnalysisResults } from '../types/frontend';

interface DreamHistoryViewProps {
  dreams: StoredDream[];
  isLoading: boolean;
  onClose: () => void;
  onViewDream: (analysis: AnalysisResults) => void;
  onDeleteDream: (id: string) => void;
}

export const DreamHistoryView: React.FC<DreamHistoryViewProps> = ({
  dreams,
  isLoading,
  onClose,
  onViewDream,
  onDeleteDream,
}) => {
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  return (
    <div className="dream-history-view">
      <div className="history-header">
        <button onClick={onClose} className="back-button">
          ← Back
        </button>
        <h2>Dream History</h2>
        <p className="history-subtitle">
          {dreams.length} {dreams.length === 1 ? 'dream' : 'dreams'} saved locally
        </p>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--soot)' }}>
          Loading dreams...
        </div>
      ) : dreams.length === 0 ? (
        <div className="history-empty">
          <p>No dreams saved yet.</p>
          <p
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--dust)',
              marginTop: 'var(--space-2)',
            }}
          >
            Your analyzed dreams will appear here.
          </p>
        </div>
      ) : (
        <div className="history-list">
          {dreams.map(dream => (
            <div key={dream.id} className="history-item">
              <div className="history-item-header">
                <span className="history-date">{formatDate(dream.timestamp)}</span>
                <button
                  onClick={() => onDeleteDream(dream.id)}
                  className="delete-button"
                  title="Delete dream"
                >
                  ×
                </button>
              </div>

              <div className="history-dream-text">{dream.dreamText}</div>

              {/* Sentiment Summary */}
              {dream.analysis.sentimentAnalysis && (
                <div
                  style={{
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--neutral-dark)',
                    marginTop: 'var(--space-3)',
                    marginBottom: 'var(--space-2)',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  <span
                    style={{
                      color:
                        dream.analysis.sentimentAnalysis.summary.dominantSentiment === 'good'
                          ? 'var(--positive)'
                          : dream.analysis.sentimentAnalysis.summary.dominantSentiment === 'bad'
                            ? 'var(--negative)'
                            : 'var(--charcoal)',
                      fontWeight: '600',
                    }}
                  >
                    {dream.analysis.sentimentAnalysis.summary.dominantSentiment}
                  </span>
                  {' • '}
                  <span>severity {dream.analysis.sentimentAnalysis.summary.aggregateSeverity}</span>
                </div>
              )}

              {/* Context Metadata */}
              {dream.analysis.originalDream.context &&
                Object.values(dream.analysis.originalDream.context).some(v => v) && (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 'var(--space-2)',
                      marginBottom: 'var(--space-3)',
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--neutral-dark)',
                    }}
                  >
                    {dream.analysis.originalDream.context.economicStatus && (
                      <span
                        style={{
                          background: 'var(--soft-sand)',
                          padding: '2px var(--space-2)',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        {dream.analysis.originalDream.context.economicStatus}
                      </span>
                    )}
                    {dream.analysis.originalDream.context.socialImportance && (
                      <span
                        style={{
                          background: 'var(--soft-sand)',
                          padding: '2px var(--space-2)',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        {dream.analysis.originalDream.context.socialImportance}
                      </span>
                    )}
                    {dream.analysis.originalDream.context.socialPosition && (
                      <span
                        style={{
                          background: 'var(--soft-sand)',
                          padding: '2px var(--space-2)',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        {dream.analysis.originalDream.context.socialPosition}
                      </span>
                    )}
                    {dream.analysis.originalDream.context.healthStatus && (
                      <span
                        style={{
                          background: 'var(--soft-sand)',
                          padding: '2px var(--space-2)',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        {dream.analysis.originalDream.context.healthStatus}
                      </span>
                    )}
                    {dream.analysis.originalDream.context.concerns && (
                      <span
                        style={{
                          background: 'var(--soft-sand)',
                          padding: '2px var(--space-2)',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        {dream.analysis.originalDream.context.concerns}
                      </span>
                    )}
                  </div>
                )}

              <div className="history-meta">
                <span>{dream.analysis.primaryRules.length} interpretations</span>
                <span>•</span>
                <span>{dream.analysis.symbolicMeanings.length} symbols</span>
                {dream.analysis.ritualRecommendations &&
                  dream.analysis.ritualRecommendations.length > 0 && (
                    <>
                      <span>•</span>
                      <span>{dream.analysis.ritualRecommendations.length} rituals</span>
                    </>
                  )}
              </div>

              <button onClick={() => onViewDream(dream.analysis)} className="view-button">
                View Analysis →
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
